import { Question, } from "survey-core";
export const CUSTOM_TRANSCRIPTION_TYPE = "custom-transcription";
export const CUSTOM_CONTEXT_TYPE = "custom-context";
export const CONTEXT_TYPES = {
    SURVEY: "survey",
    CONTACT: "contact",
    DOCUMENT: "document",
    NOTE: "note",
};
export class CustomContextQuestionModel extends Question {
    getType() {
        return CUSTOM_CONTEXT_TYPE;
    }
    get displayValue() {
        if (!this.value || !Array.isArray(this.value))
            return "";
        return this.value.map((item) => item.label).join(", ");
    }
}
export class CustomTranscriptionQuestionModel extends Question {
    getType() {
        return CUSTOM_TRANSCRIPTION_TYPE;
    }
    get displayValue() {
        if (!this.value)
            return "";
        if (typeof this.value === "string") {
            return this.value;
        }
        return this.value.text;
    }
}
export const allowedRespondentFuncParams = [
    "firstName",
    "lastName",
    "fullName",
    "indigenousStatus",
    "dateOfBirth",
    "developmentalAge",
    "sex",
    "contactEmail",
    "contactMobilePhone",
    "emailAddress",
    "company",
];
export const allowedUserProfileFuncParams = [
    "firstName",
    "lastName",
    "fullName",
    "emailAddress",
    "mobileNumber",
    "jobTitle",
    "signature",
];
export const registerCustomTranslations = (localization) => {
    const translations = localization.getLocale("");
    translations.pehelp.aiSummaryEnabled =
        "Enable AI-generated summary for survey responses.";
    translations.pehelp.aiSummaryPriorityQuestions =
        "Provide questions that the AI should prioritize when generating the summary of survey responses.";
    translations.pehelp.aiSummaryAllQuestions =
        "Include all other questions in the survey as context for the AI summary for this question.";
    translations.pehelp.aiSummaryContexts =
        "Provide additional context to guide the AI in generating a more accurate summary of survey responses.";
    translations.pehelp.aiSummaryUserPrompt =
        "Provide a prompt to guide the AI in generating a summary of survey responses.";
    translations.pehelp.defaultValueExpression = `
    <div style="white-space: normal">
      <p style="margin: 0">
        This setting allows you to assign a default answer value based on an expression.
        The expression can include:
      </p>
      <ul style="margin: 0">
        <li><strong>Basic calculations:</strong> <span>{q1<i>id</i>} + {q2<i>id</i>}</span></li>
        <li><strong>Boolean expressions:</strong> <span>{age} &gt; 60</span></li>
        <li><strong>Functions:</strong> <span>iif()</span>, <span>today()</span>, <span>age()</span>, <span>min()</span>, <span>max()</span>, <span>avg()</span>, etc.</li>
      </ul>
      <p style="margin: 0">The value determined serves as the initial default and can be overridden by manual input.</p>
      <section>
        <p style="margin: 0">Use the <span>respondent()</span> function to reference respondent fields, e.g., <span>respondent(contactEmail)</span>.</p>
        <strong>Available respondent fields:</strong>
        <ul style="margin: 0">
          <li>firstName, lastName, fullName</li>
          <li>indigenousStatus, dateOfBirth, developmentalAge, sex</li>
          <li>contactEmail, contactMobilePhone, emailAddress, company</li>
          <li>custom01 to custom10</li>
        </ul>
        <p style="margin: 0">To reference "Custom Field 1", use <span>respondent(custom01)</span>.</p>
      </section>
      <section>
        <p style="margin: 0">Use the <span>userProfile()</span> function to reference user profile fields, e.g., <span>userProfile(signature)</span>.</p>
        <strong>Available user profile fields:</strong>
        <ul style="margin: 0">
          ${allowedUserProfileFuncParams.map((p) => `<li>${p}</li>`).join("")}
        </ul>
      </section>
    </div>
  `;
};
// A text question with a special input type (date, number, email) holds a value, not prose, and has no writing style.
export const isAiFreeTextQuestion = (question) => question.getType() === "comment" ||
    (question.getType() === "text" &&
        (!question.inputType || question.inputType === "text"));
export const registerAiSerializerProperties = (serializer) => {
    // Avoid duplicate registration.
    if (serializer.findProperty("question", "aiSummaryEnabled")) {
        return;
    }
    // Register custom properties for AI summary support and prioritization
    serializer.addProperty("question", {
        name: "aiSummaryEnabled",
        type: "boolean",
        category: "ai-settings",
        default: false,
        visibleIndex: 0,
        displayName: "Enable AI Summary (Wand Button)",
    });
    serializer.addProperty("question", {
        name: "aiSummaryPriorityQuestions",
        type: "custom-ai-summary-tree", // use custom editor
        category: "ai-settings",
        default: [],
        visibleIndex: 1,
        displayName: "Context Questions for AI Summary",
        visibleIf(obj) {
            return obj.aiSummaryEnabled === true;
        },
    });
    serializer.addProperty("question", {
        name: "aiSummaryAllQuestions",
        type: "boolean",
        category: "ai-settings",
        default: false,
        visibleIndex: 2,
        displayName: "Include All Questions as Context",
        visibleIf(obj) {
            return obj.aiSummaryEnabled === true;
        },
    });
    serializer.addProperty("question", {
        name: "aiSummaryContexts",
        type: "multiplevalues",
        category: "ai-settings",
        default: [],
        visibleIndex: 3,
        displayName: "Additional Context for AI Summary",
        choices: [
            { value: "respondentDetails", text: "Respondent Details" },
            { value: "respondentNotes", text: "Respondent Notes" },
            { value: "bookingDetails", text: "Booking Details" },
        ],
        visibleIf(obj) {
            return obj.aiSummaryEnabled === true;
        },
    });
    serializer.addProperty("question", {
        name: "aiSummaryUserPrompt",
        type: "text",
        category: "ai-settings",
        default: "",
        visibleIndex: 4,
        displayName: "Custom Prompt for AI Summary",
        visibleIf(obj) {
            return obj.aiSummaryEnabled === true;
        },
    });
    serializer.addProperty("question", {
        name: "aiAnswerStyle",
        visible: false,
    });
    serializer.addProperty("survey", {
        name: "aiSummarySurveyUserPrompt",
        type: "text",
        category: "ai-settings",
        default: "",
        visibleIndex: 0,
        displayName: "Survey-level Default Prompt for AI Summary",
        visibleIf(obj) {
            // If any questions have aiSummaryEnabled = true, show this property.
            return obj
                .getAllQuestions(false, true, true)
                .some((q) => q.aiSummaryEnabled === true);
        },
    });
    serializer.addProperty("survey", {
        name: "aiWritingStyle",
        visible: false,
    });
    // Add question type metadata for further serialization into JSON
    // serializer.addClass(
    //   CUSTOM_TRANSCRIPTION_TYPE,
    //   [],
    //   () => new CustomTranscriptionQuestionModel(""),
    //   "question",
    // );
};
// export const registerContextSerializerProperties = (
//   serializer: typeof Serializer,
// ) => {
//   serializer.addClass(
//     CUSTOM_CONTEXT_TYPE,
//     [
//       {
//         name: "allowedContextTypes",
//         type: "multiplevalues",
//         category: "context-settings",
//         default: ["survey", "contact", "document", "note"],
//         displayName: "Allowed Context Types",
//         choices: [
//           { value: "survey", text: "Previous Surveys" },
//           { value: "contact", text: "Respondent Contacts" },
//           { value: "document", text: "Respondent Documents" },
//           { value: "note", text: "Respondent Notes" },
//         ],
//       },
//       {
//         name: "maxItemsPerType",
//         type: "number",
//         category: "context-settings",
//         default: 5,
//         minValue: 1,
//         maxValue: 50,
//         displayName: "Max Items Per Type",
//       },
//     ],
//     () => new CustomContextQuestionModel(""),
//     "question",
//   );
// };
export const registerCustomElements = (elementFactory) => {
    const allTypes = elementFactory.Instance.getAllTypes();
    if (!allTypes.includes(CUSTOM_TRANSCRIPTION_TYPE)) {
        elementFactory.Instance.registerElement(CUSTOM_TRANSCRIPTION_TYPE, (name) => new CustomTranscriptionQuestionModel(name));
    }
    if (!allTypes.includes(CUSTOM_CONTEXT_TYPE)) {
        elementFactory.Instance.registerElement(CUSTOM_CONTEXT_TYPE, (name) => new CustomContextQuestionModel(name));
    }
};
//# sourceMappingURL=survey.js.map